import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material'
import {
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import TargetSelector from './TargetSelector'
import {
  scheduleService,
  AssignmentTarget,
  TargetPreviewResponse,
  TestOption,
  AssignmentResult,
} from '../../services/scheduleService'

interface TestScheduleModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const TestScheduleModal: React.FC<TestScheduleModalProps> = ({ open, onClose, onSuccess }) => {
  // テスト選択
  const [tests, setTests] = useState<TestOption[]>([])
  const [selectedTest, setSelectedTest] = useState<TestOption | null>(null)
  const [loadingTests, setLoadingTests] = useState(false)

  // 対象者選択
  const [targets, setTargets] = useState<AssignmentTarget[]>([])
  const [preview, setPreview] = useState<TargetPreviewResponse | null>(null)

  // スケジュール設定
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // 状態管理
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [results, setResults] = useState<AssignmentResult[]>([])

  // テスト一覧読み込み
  useEffect(() => {
    if (!open) return

    const loadTests = async () => {
      setLoadingTests(true)
      try {
        const response = await scheduleService.getTests({ limit: 100 })
        setTests(response.tests)
      } catch (err) {
        console.error('テスト読み込みエラー:', err)
        setError('テスト一覧の取得に失敗しました')
      } finally {
        setLoadingTests(false)
      }
    }

    loadTests()
  }, [open])

  // 初期化
  useEffect(() => {
    if (open) {
      setSelectedTest(null)
      setTargets([])
      setPreview(null)
      setStartDate(getDefaultStartDate())
      setEndDate(getDefaultEndDate())
      setError(null)
      setSuccess(null)
      setResults([])
    }
  }, [open])

  const getDefaultStartDate = () => {
    const now = new Date()
    return now.toISOString().split('T')[0]
  }

  const getDefaultEndDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 14)
    return date.toISOString().split('T')[0]
  }

  const handleTargetsChange = useCallback((newTargets: AssignmentTarget[]) => {
    setTargets(newTargets)
  }, [])

  const handlePreviewUpdate = useCallback((newPreview: TargetPreviewResponse | null) => {
    setPreview(newPreview)
  }, [])

  const handleSubmit = async () => {
    if (!selectedTest) {
      setError('テストを選択してください')
      return
    }
    if (targets.length === 0) {
      setError('対象者を選択してください')
      return
    }
    if (!startDate || !endDate) {
      setError('スケジュールを設定してください')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await scheduleService.assignTestToTargets(selectedTest.id, targets, {
        startDate,
        endDate,
        dueDate: endDate,
      })

      setSuccess(response.message)
      setResults(response.results)

      setTimeout(() => {
        onSuccess()
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : '割り当てに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const isValid =
    selectedTest && targets.length > 0 && preview && preview.totalUsers > 0 && startDate && endDate

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          テスト実施スケジュール作成
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* エラー・成功メッセージ */}
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {/* テスト選択 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AssignmentIcon fontSize="small" />
              テスト選択
            </Typography>
            <Autocomplete
              options={tests}
              value={selectedTest}
              onChange={(_, newValue) => {
                setSelectedTest(newValue)
                setError(null)
              }}
              loading={loadingTests}
              disabled={loading || success !== null}
              getOptionLabel={(option) => option.title}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.complianceRule.category} | 問題数: {option.questionCount}問 | 合格点:{' '}
                      {option.passingScore}%
                      {option.timeLimit && ` | 制限時間: ${option.timeLimit}分`}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="テストを選択"
                  placeholder="テスト名で検索"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingTests && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {selectedTest && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {selectedTest.title}
                </Typography>
                {selectedTest.description && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedTest.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip size="small" label={selectedTest.complianceRule.category} />
                  <Typography variant="caption">問題数: {selectedTest.questionCount}問</Typography>
                  <Typography variant="caption">合格点: {selectedTest.passingScore}%</Typography>
                  {selectedTest.timeLimit && (
                    <Typography variant="caption">制限時間: {selectedTest.timeLimit}分</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>

          {/* 対象者選択 */}
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <EventIcon fontSize="small" />
              対象者選択
            </Typography>
            <TargetSelector
              targets={targets}
              onTargetsChange={handleTargetsChange}
              preview={preview}
              onPreviewUpdate={handlePreviewUpdate}
              disabled={loading || success !== null}
            />
          </Box>

          {/* スケジュール設定 */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <ScheduleIcon fontSize="small" />
              スケジュール設定
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel htmlFor="start-date">開始日</InputLabel>
                <OutlinedInput
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  label="開始日"
                  disabled={loading || success !== null}
                />
                <FormHelperText>テスト受講可能開始日</FormHelperText>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel htmlFor="end-date">終了日</InputLabel>
                <OutlinedInput
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  label="終了日"
                  disabled={loading || success !== null}
                  inputProps={{ min: startDate }}
                />
                <FormHelperText>テスト受講可能終了日・リマインダー通知基準日</FormHelperText>
              </FormControl>
            </Box>
          </Paper>

          {/* 割り当て結果 */}
          {results.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                割り当て結果
              </Typography>
              <List dense>
                {results.map((result) => (
                  <ListItem key={result.groupEnrollmentId}>
                    <ListItemIcon>
                      {result.status === 'assigned' ? (
                        <CheckCircleIcon color="primary" />
                      ) : (
                        <ErrorIcon color="secondary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.groupName}
                      secondary={
                        result.status === 'assigned'
                          ? `${result.assignedCount}名に割り当て完了`
                          : result.message || 'アクティブなメンバーがいません'
                      }
                    />
                    <Chip
                      size="small"
                      label={result.status === 'assigned' ? '成功' : 'エラー'}
                      color={result.status === 'assigned' ? 'success' : 'error'}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          {success ? '閉じる' : 'キャンセル'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isValid}
            startIcon={loading ? <CircularProgress size={20} /> : <ScheduleIcon />}
          >
            {loading
              ? '割り当て中...'
              : `スケジュール作成${preview ? ` (${preview.totalUsers}名)` : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default TestScheduleModal
